import { types, flow, applySnapshot, getSnapshot } from 'mobx-state-tree';
import * as clipboard from 'clipboard-polyfill';
import keyboardjs from 'keyboardjs';
import PubSub from 'pubsub-js';

import analytics from 'js/services/Analytics';
import { navigate, errorResponse, makeRequest, url } from 'js/services/Network';
import ShipmentTracking from 'js/stores/types/ShipmentTracking';
import ShippingInstructions from 'js/stores/types/ShippingInstructions';
import OrderOffer from 'js/stores/types/OrderOffer';
import Facebook from 'js/services/Facebook';
import VFDVirtualAccount from '../types/VFDVirtualAccount';

export default types
  .model('ItemStatusPage', {
    mode: 'item',
    modalIsOpen: false,
    showManageMenu: false,
    showDeleteModal: false,
    showCreditCardModal: false,
    howItWorksIsOpen: false,
    isDeleting: false,
    isSavingCard: false,
    isCompleting: false,
    error: types.optional(types.union(types.string, types.null), null),
    credit_card_error: types.optional(types.union(types.string, types.null), null),
    alias: types.string,
    deleted_at: types.optional(types.union(types.null, types.string, types.boolean), null),
    my_relation: '',
    seller: '',
    price: types.optional(types.union(types.number, types.string), 0),
    title: '',
    primary_image: '',
    was_sold: false,
    quantity: 0,
    offers: types.array(OrderOffer),
    orders: types.array(OrderOffer),
    payment_link_url: '',
  })
  .views(self => ({
    get is_seller() {
      return true;
    },
    get is_buyer() {
      return self.my_relation === 'buyer';
    },
    get can_modify() {
      return self.is_seller;
    },
    get can_delete() {
      return !self.has_orders;
    },
    get has_orders() {
      return !!self.orders.length > 0;
    },
    get has_offers() {
      return self.offers.length > 0;
    },
    get actual_price() {
      return self.order_item_price || self.price;
    },
  }))
  .actions(self => {
    let bodyClick = null;

    return {
      afterCreate() {
        const urlParams = new URLSearchParams(window.location.search);
        self.modalIsOpen = urlParams.has('change-price');
        self.showSoldModal = urlParams.has('mark-sold');

        keyboardjs.bind('esc', self.closeAll);
        bodyClick = PubSub.subscribe('body-click', self.closeAll);
      },
      beforeDestroy() {
        keyboardjs.unbind('esc', self.closeAll);
        PubSub.unsubscribe(bodyClick);
      },

      returnOrder() {
        navigate(url('return-order-page', { uid: self.order_uid }));
      },
      openModal(event) {
        event && event.stopPropagation();
        self.modalIsOpen = true;
        self.showManageMenu = false;
        return self;
      },
      toggleManageMenu(event) {
        event && event.stopPropagation();
        self.showManageMenu = !self.showManageMenu;
        return self;
      },
      showHowItWorks(event) {
        event && event.stopPropagation();
        self.howItWorksIsOpen = true;
        return self;
      },
      afterSave(response) {
        applySnapshot(self, { ...getSnapshot(self), ...response.data });
        self.modalIsOpen = false;
      },
      closeModal() {
        self.modalIsOpen = false;
        self.showCreditCardModal = false;
        self.howItWorksIsOpen = false;
        return self;
      },
      closeAll() {
        self.modalIsOpen = false;
        self.howItWorksIsOpen = false;
        self.showManageMenu = false;
        self.showSoldModal = false;
      },
      tokenSaved(data) {
        self.modalIsOpen = false;
        self.title = data.title;
        self.price = data.price;
        return self;
      },
      copyLink: flow(function* copyLink() {
        // analytics.track('PLA_copied link');
        self.showManageMenu = false;
        try {
          yield clipboard.writeText(self.payment_link_url);
          PubSub.publish('popup-text', 'Link copied!');
        } catch (error) {}
      }),
      shareLink() {
        const { title, payment_link_url: url } = self;
        if (navigator.share) {
          navigator.share({
            title,
            url,
            text: `Here is my delivery link, you can pay there and arrange delivery`,
          });
        } else {
          Facebook.ui({
            method: 'share',
            href: url,
          });
        }
      },

      updateCreditCard() {
        self.showCreditCardModal = true;
        return self;
      },
      saveCreditCard: flow(function* saveCreditCard(values) {
        let response;

        if (!self.isSavingCard) {
          self.credit_card_error = null;
          self.isSavingCard = true;
          try {
            response = yield makeRequest('status-update-credit-card', { uid: self.order_uid }, values);
            applySnapshot(self, { ...getSnapshot(self), ...response.data });
            self.showCreditCardModal = false;
          } catch (error) {
            self.credit_card_error = errorResponse(error);
          }
        }
        self.isSavingCard = false;

        return response;
      }),

      promptDelete() {
        self.showDeleteModal = true;
        return self;
      },
      closeDeleteModal() {
        self.showDeleteModal = false;
        return self;
      },
      deleteItem: flow(function* deleteItem() {
        if (!self.isDeleting) {
          self.isDeleting = true;
          try {
            yield makeRequest('delete-item', self);
            navigate(url('my-items-page'));
          } catch (error) {
            self.error = errorResponse(error);
          }
        }
        self.isDeleting = false;
      }),

      onUpload(image_url) {
        self.primary_image = image_url;
      },
      setError(message) {
        self.error = message;
        return self;
      },
    };
  });
